import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/AuthContext";
import { Card, Btn, Pill, Modal, Empty, Notice, inputCls } from "@/dashboard/ui";
import { RecipientPicker } from "@/dashboard/Marketing";
import { Repeat, Plus, Pencil, Trash2, Play, Pause, XCircle, Users } from "lucide-react";

const STEP_TONE = { active: "green", paused: "amber", completed: "grey", cancelled: "outline" };

export default function SequencesPanel() {
  const [seqs, setSeqs] = useState(null);
  const [runnerEnabled, setRunnerEnabled] = useState(false);
  const [editing, setEditing] = useState(null);
  const [enrolling, setEnrolling] = useState(null);
  const [enrollIds, setEnrollIds] = useState([]);
  const [enrollments, setEnrollments] = useState(null);

  const load = () => api.get("/followups/sequences").then((r) => {
    setSeqs(r.data.sequences);
    setRunnerEnabled(r.data.runner_enabled);
  }).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const blank = { name: "", steps: [{ delay_hours: 24, subject: "", body: "" }] };

  const save = async () => {
    try {
      if (editing.id) await api.put(`/followups/sequences/${editing.id}`, editing);
      else await api.post("/followups/sequences", editing);
      toast.success("Sequence saved — it stays off until you enable it");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggle = async (s) => {
    const { data } = await api.post(`/followups/sequences/${s.id}/toggle`);
    toast.success(data.enabled ? "Sequence enabled — you can now enrol contacts" : "Sequence disabled");
    load();
  };

  const enroll = async () => {
    try {
      const { data } = await api.post(`/followups/sequences/${enrolling.id}/enroll`, { lead_ids: enrollIds });
      toast.success(`${data.enrolled} enrolled${data.skipped_opted_out ? ` · ${data.skipped_opted_out} skipped (opted out or no email)` : ""}`);
      setEnrolling(null);
      setEnrollIds([]);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const showEnrollments = async (s) => {
    const { data } = await api.get(`/followups/sequences/${s.id}/enrollments`);
    setEnrollments({ s, list: data.enrollments });
  };

  const enrollAction = async (eid, action) => {
    try {
      await api.post(`/followups/enrollments/${eid}/${action}`);
      toast.success(`Enrollment ${action}d`);
      showEnrollments(enrollments.s);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="sequences-panel">
      <Notice testid="sequences-note"
        title={runnerEnabled ? "Follow-up runner is ON" : "Follow-up automation is OFF"}
        body={runnerEnabled
          ? "Enabled sequences send their due steps on schedule. Contacts who unsubscribe are removed at the next step automatically."
          : "Sequences can be built and contacts enrolled, but no follow-up email is sent until the platform owner enables automation. Consent is re-checked before every step."} />
      <div className="flex justify-end">
        <Btn data-testid="sequence-new-button" onClick={() => setEditing(JSON.parse(JSON.stringify(blank)))}>
          <Plus size={15} /> New sequence
        </Btn>
      </div>

      {seqs === null ? <p className="py-8 text-center text-sm text-neutral-400">Loading…</p> : seqs.length === 0 ? (
        <Empty testid="sequences-empty" icon={Repeat} title="No follow-up sequences yet"
          body="Build a sequence of timed follow-up emails, enable it, then enrol CRM contacts. Everything stays off until you switch it on." />
      ) : (
        <div className="space-y-3">
          {seqs.map((s) => (
            <Card key={s.id} className="p-5" data-testid={`sequence-card-${s.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-bold text-[#111111]">{s.name}</p>
                  <Pill tone={s.enabled ? "green" : "grey"} data-testid={`sequence-state-${s.id}`}>{s.enabled ? "Enabled" : "Off"}</Pill>
                  <Pill tone="outline">{s.steps.length} step{s.steps.length === 1 ? "" : "s"}</Pill>
                  <Pill tone="outline">{s.enrolled} active enrolment{s.enrolled === 1 ? "" : "s"}</Pill>
                </div>
                <div className="flex gap-1">
                  <button className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100" title="Enrolments" data-testid={`sequence-enrollments-${s.id}`} onClick={() => showEnrollments(s)}><Users size={14} /></button>
                  <button className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100" title="Edit" data-testid={`sequence-edit-${s.id}`} onClick={() => setEditing(JSON.parse(JSON.stringify(s)))}><Pencil size={14} /></button>
                  <button className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-red-50 hover:text-red-600" title="Delete" data-testid={`sequence-delete-${s.id}`}
                    onClick={async () => { if (window.confirm(`Delete sequence "${s.name}"? Active enrolments are cancelled.`)) { await api.delete(`/followups/sequences/${s.id}`); load(); } }}><Trash2 size={14} /></button>
                </div>
              </div>
              <div className="mt-2 space-y-1">
                {s.steps.map((st, i) => (
                  <p key={i} className="text-xs text-neutral-500">Step {i + 1}: after {st.delay_hours}h — {st.subject}</p>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Btn variant="ghost" data-testid={`sequence-toggle-${s.id}`} onClick={() => toggle(s)}>
                  {s.enabled ? <><Pause size={13} className="mr-1 inline" /> Disable</> : <><Play size={13} className="mr-1 inline" /> Enable</>}
                </Btn>
                {s.enabled && (
                  <Btn variant="ghost" data-testid={`sequence-enroll-${s.id}`} onClick={() => { setEnrolling(s); setEnrollIds([]); }}>
                    <Users size={13} className="mr-1 inline" /> Enrol contacts
                  </Btn>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <Modal title={editing.id ? "Edit sequence" : "New follow-up sequence"} onClose={() => setEditing(null)} testid="sequence-modal">
          <div className="max-h-[70vh] space-y-3 overflow-y-auto pr-1">
            <input className={inputCls} placeholder="Sequence name *" value={editing.name} data-testid="seq-name" onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))} />
            {editing.steps.map((st, i) => (
              <div key={i} className="space-y-2 rounded-xl border border-neutral-200 p-3" data-testid={`seq-step-${i}`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold text-neutral-700">Step {i + 1}</p>
                  {editing.steps.length > 1 && (
                    <button className="text-neutral-400 hover:text-red-600" data-testid={`seq-step-remove-${i}`}
                      onClick={() => setEditing((s) => ({ ...s, steps: s.steps.filter((_, j) => j !== i) }))}><Trash2 size={13} /></button>
                  )}
                </div>
                <label className="block">
                  <span className="mb-0.5 block text-[11px] text-neutral-500">Wait (hours after previous step)</span>
                  <input type="number" min="1" className={inputCls} value={st.delay_hours} data-testid={`seq-delay-${i}`}
                    onChange={(e) => setEditing((s) => ({ ...s, steps: s.steps.map((x, j) => j === i ? { ...x, delay_hours: Math.max(1, parseInt(e.target.value) || 1) } : x) }))} />
                </label>
                <input className={inputCls} placeholder="Email subject *" value={st.subject} data-testid={`seq-subject-${i}`}
                  onChange={(e) => setEditing((s) => ({ ...s, steps: s.steps.map((x, j) => j === i ? { ...x, subject: e.target.value } : x) }))} />
                <textarea rows={3} className={inputCls} placeholder="Email body * (use {{name}})" value={st.body} data-testid={`seq-body-${i}`}
                  onChange={(e) => setEditing((s) => ({ ...s, steps: s.steps.map((x, j) => j === i ? { ...x, body: e.target.value } : x) }))} />
              </div>
            ))}
            <Btn variant="ghost" data-testid="seq-add-step" disabled={editing.steps.length >= 10}
              onClick={() => setEditing((s) => ({ ...s, steps: [...s.steps, { delay_hours: 48, subject: "", body: "" }] }))}>
              <Plus size={13} className="mr-1 inline" /> Add step
            </Btn>
            <div className="flex justify-end gap-2">
              <Btn variant="ghost" onClick={() => setEditing(null)}>Cancel</Btn>
              <Btn data-testid="seq-save" disabled={!editing.name.trim() || editing.steps.some((s) => !s.subject.trim() || !s.body.trim())} onClick={save}>Save sequence</Btn>
            </div>
          </div>
        </Modal>
      )}

      {enrolling && (
        <Modal title={`Enrol contacts — ${enrolling.name}`} onClose={() => setEnrolling(null)} testid="enroll-modal">
          <RecipientPicker selected={enrollIds} onChange={setEnrollIds} />
          <p className="mt-2 text-[11px] text-neutral-400">Contacts who opted out or have no email address are skipped automatically. Enrolling the same contact twice never duplicates messages.</p>
          <div className="mt-3 flex justify-end gap-2">
            <Btn variant="ghost" onClick={() => setEnrolling(null)}>Cancel</Btn>
            <Btn data-testid="enroll-confirm" disabled={enrollIds.length === 0} onClick={enroll}>Enrol {enrollIds.length}</Btn>
          </div>
        </Modal>
      )}

      {enrollments && (
        <Modal title={`Enrolments — ${enrollments.s.name}`} onClose={() => setEnrollments(null)} testid="enrollments-modal">
          <div className="max-h-[60vh] space-y-2 overflow-y-auto" data-testid="enrollments-list">
            {enrollments.list.length === 0 ? <p className="text-sm text-neutral-500">No contacts enrolled yet.</p> :
              enrollments.list.map((e) => (
                <div key={e.id} className="flex items-center justify-between gap-2 rounded-xl border border-neutral-200 px-3.5 py-2.5" data-testid={`enrollment-${e.id}`}>
                  <div>
                    <p className="text-xs font-semibold text-[#111111]">{e.lead_name}</p>
                    <p className="text-[11px] text-neutral-500">Step {e.step + 1} · next {String(e.next_at || "").slice(0, 16).replace("T", " ")}</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Pill tone={STEP_TONE[e.status]}>{e.status}</Pill>
                    {e.status === "active" && <button className="text-neutral-400 hover:text-amber-600" title="Pause" data-testid={`enrollment-pause-${e.id}`} onClick={() => enrollAction(e.id, "pause")}><Pause size={14} /></button>}
                    {e.status === "paused" && <button className="text-neutral-400 hover:text-green-700" title="Resume" data-testid={`enrollment-resume-${e.id}`} onClick={() => enrollAction(e.id, "resume")}><Play size={14} /></button>}
                    {["active", "paused"].includes(e.status) && <button className="text-neutral-400 hover:text-red-600" title="Cancel" data-testid={`enrollment-cancel-${e.id}`} onClick={() => enrollAction(e.id, "cancel")}><XCircle size={14} /></button>}
                  </div>
                </div>
              ))}
          </div>
        </Modal>
      )}
    </div>
  );
}
